/**
 * `AgentProvider` — the terminal state machine, ported from the parked
 * `AgentProvider.tsx` and rewired onto `@paradigm/terminal-core` factories
 * plus a host-supplied `TerminalConfig` (read from context).
 *
 * Responsibilities (all preserved from the original):
 *  - Connection lifecycle (connect / disconnect / reconnect-on-account-change)
 *  - Auth + self-service pairing (delegated to `createOpenClawClient`)
 *  - Per-session frame routing (runId → session ownership + background
 *    buffering) so an in-flight run keeps writing to its originating chat
 *  - Processing indicator, `/model` echo suppression
 *  - Sessions / folders CRUD, hydrate-on-account-change
 *
 * Remote session-sync is NATIVE (not host-injected): on hydration we pull the
 * gateway's `/sync/v1` snapshot, merge it into the chat-history store, and push
 * local changes. It degrades to local-only when the gateway has no sync API.
 */
import {
  generateMessageId,
  generateSessionId,
  SESSION_TITLE_MAX_LEN,
  createOpenClawClient,
  createSessionSync,
  estimateChatSendBytes,
  hydrateFromRemote,
  fetchAndMergeSessionFromRemote,
  buildIndexDoc,
  MAX_CHAT_SEND_BYTES,
} from '@paradigm/terminal-core';
import type {
  AgentConnectionStatus,
  AgentWebSocket,
  ChatAttachment,
  ChatMessage,
  ChatSession,
  CustomFolder,
  GatewayFrame,
  MessageAttachment,
  ModelInfo,
  SessionFolder,
  TerminalSync,
} from '@paradigm/terminal-core';
import { deriveHttpBase, fetchGatewayModels } from '../models/gateway-models';
import {
  defaultModelLabel,
  modelIdsMatch,
  pickDefaultModelId,
  resolveModelInCatalog,
} from '../models/model-labels';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { useTerminalConfig } from '../context/config-context';
import { buildTerminalStores } from '../config';
import { buildSendPayload } from '../send-payload';
import {
  AgentActionsProvider,
  AgentStateProvider,
  INITIAL_AGENT_STATE,
  type AgentActions,
  type AgentState,
} from './contexts';
import { handleFrame } from './handleFrame';

interface Props {
  readonly children: ReactNode;
}

const DEFAULT_SESSION_TITLE = 'New Chat';

/**
 * Model fields of a gateway session row (`sessions.describe` /
 * `sessions.list`). The gateway resolves these from the session's stored
 * override, else its runtime model, else the agent default — so for an
 * existing session they reflect what a send would actually run.
 */
interface GatewaySessionModelRef {
  readonly modelProvider?: string | null;
  readonly model?: string | null;
}

interface SessionsDescribeResult {
  readonly session?: GatewaySessionModelRef | null;
}

interface SessionsListDefaultsResult {
  readonly defaults?: GatewaySessionModelRef | null;
}

/**
 * Compose a session row's split provider/model fields back into the
 * picker's catalog id shape. The catalog ids are provider-qualified
 * (`dt-a/bedrock/anthropic/opus/4.8`) while gateway rows return
 * `modelProvider: 'dt-a'` and `model: 'bedrock/anthropic/opus/4.8'`
 * separately — the same wrapping openclaw uses internally.
 */
function composeSessionModelId(
  ref: GatewaySessionModelRef | null | undefined,
): string | null {
  const model = ref?.model?.trim();
  if (model == null || model.length === 0) return null;
  const provider = ref?.modelProvider?.trim();
  return provider != null && provider.length > 0
    ? `${provider}/${model}`
    : model;
}

/**
 * Map a gateway-reported model id onto the live catalog for display: the
 * catalog's exact id when it carries an equivalent, else the raw id. The
 * picker resolves the label through the same helper, so state and trigger
 * can no longer name different models.
 */
function reconcileModelIdWithCatalog(
  modelId: string,
  models: readonly ModelInfo[],
): string {
  return resolveModelInCatalog(models, modelId)?.id ?? modelId;
}

/**
 * Resolve an incoming frame's `sessionKey` to one of the UI's known session
 * ids. The gateway echoes the key namespaced as `agent:<channel>:<uiSessionId>`
 * (e.g. `agent:main:<uuid>`, `agent:cron:<uuid>`), but the UI tracks bare
 * session ids. So we:
 *   1. Prefer a whole-key exact match — an id that arrives un-namespaced (even
 *      one that itself contains colons) wins outright and is never split.
 *   2. Otherwise strip the `agent:<channel>:` prefix and match what remains.
 *      We keep EVERYTHING after the channel segment (not just the trailing
 *      `:`-segment), so an id containing colons still resolves and a prefix
 *      collision like ids `{'id', 'session:id'}` with key `agent:main:session:id`
 *      resolves to `'session:id'`, never the shorter `'id'`.
 * Returns the matched session id, or null when nothing resolves (the caller
 * then falls back to run-ownership / last-send heuristics).
 */
export function matchKnownSessionKey(
  rawKey: string | null | undefined,
  knownSessionIds: ReadonlySet<string>,
): string | null {
  if (rawKey == null) return null;
  if (knownSessionIds.has(rawKey)) return rawKey;
  const parts = rawKey.split(':');
  if (parts[0] === 'agent' && parts.length >= 3) {
    const sessionId = parts.slice(2).join(':');
    if (sessionId.length > 0 && knownSessionIds.has(sessionId)) {
      return sessionId;
    }
  }
  return null;
}

/**
 * Whether a frame belongs to a session that NOTHING on this device knows
 * about: its `sessionKey` resolves to no locally known session AND its run
 * isn't one we've already claimed. These frames come from a session created
 * on another device (all devices share one gateway, which fans events out to
 * every connection, but the sync index only pulls on (re)connect — so a
 * session born mid-connection is invisible here until the next snapshot
 * merge). Foreign frames must never fall through to the last-send /
 * active-session heuristics: that's exactly the cross-device leak where a
 * mobile chat lands in an unrelated desktop thread.
 */
export function isForeignSessionFrame(
  payload: { readonly sessionKey?: string; readonly runId?: string },
  knownSessionIds: ReadonlySet<string>,
  ownedRunIds: { has(runId: string): boolean },
): boolean {
  const key = payload.sessionKey;
  if (key == null || key.length === 0) return false;
  if (matchKnownSessionKey(key, knownSessionIds) != null) return false;
  return payload.runId == null || !ownedRunIds.has(payload.runId);
}

function deriveSessionTitle(content: string): string {
  const oneLine = content.replace(/\s+/gu, ' ').trim();
  if (oneLine.length <= SESSION_TITLE_MAX_LEN) return oneLine;
  return `${oneLine.slice(0, SESSION_TITLE_MAX_LEN - 1)}…`;
}

/**
 * Pick which session should be active after hydration. Prefer the
 * persisted `activeSessionId` (still valid only if the session still
 * exists), otherwise the most-recently-updated session, otherwise null.
 */
function resolveActiveSession(
  sessions: readonly ChatSession[],
  persistedActiveId: string | null,
): string | null {
  if (sessions.length === 0) return null;
  if (
    persistedActiveId != null &&
    sessions.some((s) => s.sessionId === persistedActiveId)
  ) {
    return persistedActiveId;
  }
  const mostRecent = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt)[0];
  return mostRecent?.sessionId ?? null;
}

function closeStreamingMessages(
  messages: readonly ChatMessage[],
): readonly ChatMessage[] {
  if (!messages.some((m) => m.isStreaming === true)) return messages;
  return messages.map((m) =>
    m.isStreaming === true ? { ...m, isStreaming: false } : m,
  );
}

// Storage bucket sent to the sync server as `X-Sync-User`. A single shared
// value across every surface (standalone / Paradex / Paradigm) so sessions are
// identical once they all embed this package. Token-mode gateways key storage
// on this header; trusted-proxy gateways ignore it (they use the wallet
// address). Auth itself stays on the gateway token for now.
const SYNC_USER = 'default';

// Restore (snapshot pull) retry policy. Restore fires when the connection
// reaches `authenticated`; a null result is either a transient failure or a
// gateway without `/sync/v1`. We retry a bounded number of times so transient
// blips recover while a no-sync gateway settles to local-only quickly.
const RESTORE_MAX_ATTEMPTS = 4;
const RESTORE_RETRY_BASE_MS = 1_000;

// Model-catalog retry policy. `fetchGatewayModels` collapses every failure
// (network blip, 502 `models_unavailable` from a cold sidecar, not-yet-seeded
// gateway token) into an empty list, so an empty result is the retry signal.
// The gateway is the ONLY source of models, so giving up means the user cannot
// switch models for the rest of the connection — retry generously. Backoff is
// linear (base × attempt) like restore, capped so the tail doesn't stretch
// into minutes: 1+2+3+4+5+5+5 ≈ 25s across 8 attempts.
const MODELS_MAX_ATTEMPTS = 8;
const MODELS_RETRY_BASE_MS = 1_000;
const MODELS_RETRY_MAX_MS = 5_000;

// Model-switch verification. A pick flips the picker optimistically and
// sends `/model <id>`; the gateway is then read back silently via
// `sessions.describe` to confirm the override actually landed (the command
// is processed as part of chat.send handling, so the first check rarely
// misses; one retry covers a slow store write). On confirmed mismatch the
// picker bounces back to whatever the gateway reports.
const MODEL_VERIFY_DELAY_MS = 1_500;
const MODEL_VERIFY_RETRY_MS = 2_500;
const MODEL_VERIFY_MAX_ATTEMPTS = 2;

// How long a transient error stays up before it self-dismisses.
const TRANSIENT_ERROR_MS = 5_000;

// Throttle for the foreign-frame snapshot pull. A streaming run on another
// device emits many frames for its (locally unknown) session, and that
// device's index upload is itself debounced — so the first pull can
// legitimately miss and a retry on a later frame is expected. Single-flight +
// rate-limited so the frame stream can't hammer `/sync/v1/snapshot`.
const FOREIGN_HYDRATE_THROTTLE_MS = 5_000;

function buildSessionContextMessage(
  sessionContext: Record<string, string>,
): string {
  const body = Object.entries(sessionContext)
    .map(([k, v]) => `${k}=${v}`)
    .join(';');
  return `<session-context>${body}</session-context>`;
}

/**
 * Whether the terminal has enough to show (vs. its login screen). A gateway URL
 * is always required. Token-only hosts (`supportsPairingRole === false`, e.g.
 * Paradigm) additionally need a token — stored, or supplied via config
 * `fallbackPairingToken`. Pairing-role hosts self-pair from the URL alone, so a
 * token isn't required up front.
 */
function deriveIsPaired(opts: {
  readonly account: string | null;
  readonly url: string | null;
  readonly supportsPairingRole?: boolean;
  readonly hasStoredToken: boolean;
  readonly fallbackPairingToken?: string;
}): boolean {
  if (opts.account == null) return false;
  if (opts.url == null || opts.url.length === 0) return false;
  if (opts.supportsPairingRole !== false) return true;
  return opts.hasStoredToken || (opts.fallbackPairingToken?.length ?? 0) > 0;
}

export function AgentProvider({ children }: Props) {
  const config = useTerminalConfig();

  // Stores are stable per namespace; the component memoizes on that key.
  const { urlStore, historyStore, deviceStore } = useMemo(
    () => buildTerminalStores(config),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.namespace],
  );

  const accountId = config.accountId;
  const defaultFolder = (config.defaultFolder ??
    config.namespace) as SessionFolder;

  const accountIdRef = useRef<string | null>(accountId);
  accountIdRef.current = accountId;

  // Seed URL + pairing synchronously from storage/config, before the async
  // hydrate effect runs, so (1) a returning paired user goes straight to the
  // terminal with no login flash, and (2) the login screen prefills the known
  // gateway URL — matching what the settings screen shows.
  //
  // `agentUrl` is seeded ONLY when unpaired (login screen). When paired, we
  // leave it null and let hydrate set it alongside the initial connect;
  // seeding it here would make ChatInterface's mount-connect fire a duplicate.
  const [state, setState] = useState<AgentState>(() => {
    const initialUrl =
      accountId != null
        ? (urlStore.getAgentUrl(accountId) ?? config.gatewayUrl ?? null)
        : null;
    const initialPaired = deriveIsPaired({
      account: accountId,
      url: initialUrl,
      supportsPairingRole: config.supportsPairingRole,
      hasStoredToken:
        accountId != null && urlStore.getAgentToken(accountId) != null,
      fallbackPairingToken: config.fallbackPairingToken,
    });
    return {
      ...INITIAL_AGENT_STATE,
      agentUrl: initialPaired ? null : initialUrl,
      isPaired: initialPaired,
    };
  });
  const wsRef = useRef<AgentWebSocket | null>(null);

  // True after a deliberate `disconnect()` (user hit Disconnect), cleared by any
  // intentional `connect()`. The idle-tab auto-reconnect effect checks this so
  // it recovers passive drops but never silently reopens a socket the user
  // closed on purpose.
  const manualDisconnectRef = useRef(false);

  // Set synchronously at the top of `connect()` and cleared once the attempt
  // settles (first status callback, early bail-out, or failure). It's the guard
  // the auto-reconnect effect actually needs: `connect()` flips `status` to
  // 'connecting' only asynchronously (after `await resolveUrl`) and `statusRef`
  // lags React commits, so status alone can't prevent a second wake event from
  // entering `connect()` while the first is mid-flight and leaking a socket.
  const isConnectingRef = useRef(false);
  // Timestamp (ms) of the last auto-reconnect attempt, for cross-event backoff
  // so a flapping network / rapid alt-tabbing can't spawn a fresh client (and
  // reset its retry budget) on every wake event.
  const lastReconnectAtRef = useRef(0);

  // Stash the latest config bits behind refs so the memoized `connect`
  // closure always sees current values without being re-created on
  // every render.
  const configRef = useRef(config);
  configRef.current = config;

  const generativeUiEnabledRef = useRef(config.features?.generativeUi ?? false);
  generativeUiEnabledRef.current = config.features?.generativeUi ?? false;

  const reportError = useCallback((err: unknown) => {
    configRef.current.logException?.(err);
  }, []);

  /**
   * Diagnostics for the per-session model paths. These failures are all
   * RECOVERABLE by design — the picker keeps a stale or default value and the
   * next switch/reconnect re-reads the truth — so they must not reach
   * `logException`, which is the host's exception channel (a mid-describe
   * reconnect is not a bug worth paging anyone for). But swallowing them
   * silently left "the picker shows the wrong model" with no way to tell
   * which read failed, so they go to the connection-diagnostics hook.
   */
  const logModelDebug = useCallback(
    (event: string, detail?: Record<string, unknown>) => {
      configRef.current.onConnectionDebug?.(event, detail);
    },
    [],
  );

  /**
   * Surface a transient error and schedule its dismissal. The single owner of
   * that pairing: three hand-rolled copies had drifted, and the newest matched
   * on a message PREFIX, so a stale timer dismissed a newer toast early.
   * Clearing is keyed on exact identity, so a timer only ever retires the
   * message it was armed for. Safe to pass as `handleFrame`'s
   * `onTransientError` — re-setting the same string is a no-op.
   */
  const showTransientError = useCallback((msg: string) => {
    setState((prev) =>
      prev.transientError === msg ? prev : { ...prev, transientError: msg },
    );
    setTimeout(() => {
      setState((prev) =>
        prev.transientError === msg ? { ...prev, transientError: null } : prev,
      );
    }, TRANSIENT_ERROR_MS);
  }, []);

  // Remember the last URL we pushed to the consumer's persistence so
  // repeated reconnects with the same URL don't spam the callback.
  const lastPersistedUrlRef = useRef<string | null>(null);

  // Track the identity we've already sent a `<session-context>` for over
  // the current WS instance, so heartbeat-driven reconnects don't repeatedly
  // re-introduce the operator. Cleared whenever the socket drops below
  // 'authenticated', so the *next* successful auth always re-sends.
  const sessionContextSentForRef = useRef<string | null>(null);

  // Per-run session ownership: runId → sessionId.
  const runOwnershipRef = useRef<Map<string, string>>(new Map());

  // In-memory message buffer for sessions that are receiving frames while
  // not active.
  const backgroundSessionsRef = useRef<Map<string, readonly ChatMessage[]>>(
    new Map(),
  );

  // Parallel buffer for the "Thinking" indicator of inactive sessions. Without
  // it, the user switching back to an in-flight chat sees their question but
  // no loading state — the work IS still in flight, but the UI signal vanishes
  // because `processing` only lives on the live state object.
  const backgroundProcessingRef = useRef<Map<string, AgentState['processing']>>(
    new Map(),
  );

  const activeSessionIdRef = useRef<string | null>(null);
  activeSessionIdRef.current = state.activeSessionId;

  const cancelledRunIdsRef = useRef<ReadonlySet<string>>(new Set<string>());
  cancelledRunIdsRef.current = state.cancelledRunIds;

  const suppressedModelEchoRunIdsRef = useRef<Set<string>>(new Set());

  /**
   * The active chat's model, DERIVED rather than stored. This precedence chain
   * is the whole policy, in one place:
   *
   *   1. the active session's own model — the gateway is the truth for a chat
   *      that exists
   *   2. a pick made before any session existed — an explicit user choice
   *      outranks anything we merely resolved
   *   3. the gateway main session's model — display-only, so a fresh install
   *      shows something real (never what a chat runs; another surface may
   *      have set it)
   *   4. the gateway default, then the catalog's Opus-first default
   *
   * Expressing it as an ordering is what removed the guard choreography: five
   * call sites used to write `selectedModelId` directly, each needing its own
   * "is this still the active session / has a newer pick landed" check, and a
   * missed check meant the picker named a model the session was not running.
   * A late read-back now writes its own session's slot and simply loses.
   */
  /**
   * The ONLY writer of per-session model state. Everything that learns a
   * session's model — a pick, a read-back, a verify result, a seed — goes
   * through here, so the keyed store cannot disagree with what the picker
   * shows (the picker reads it through the derivation above).
   */
  const applySessionModel = useCallback(
    (sessionId: string, modelId: string): void => {
      setState((prev) =>
        prev.sessionModels[sessionId] === modelId
          ? prev
          : {
              ...prev,
              sessionModels: { ...prev.sessionModels, [sessionId]: modelId },
            },
      );
    },
    [],
  );

  const selectedModelId = useMemo<string | null>(() => {
    const active = state.activeSessionId;
    const raw =
      (active != null ? state.sessionModels[active] : undefined) ??
      state.pendingModelId ??
      state.mainSessionModelId ??
      state.gatewayDefaultModelId ??
      pickDefaultModelId(state.models);
    return raw == null ? null : reconcileModelIdWithCatalog(raw, state.models);
  }, [
    state.activeSessionId,
    state.sessionModels,
    state.pendingModelId,
    state.mainSessionModelId,
    state.gatewayDefaultModelId,
    state.models,
  ]);

  const selectedModelIdRef = useRef<string | null>(null);
  selectedModelIdRef.current = selectedModelId;

  // Whole-state mirror, for the few callbacks that need to READ model state
  // synchronously (the pick's no-op guard, the seed's default comparison)
  // without threading it through a setState updater. Read-only by convention —
  // every write still goes through setState.
  const stateRef = useRef<AgentState>(state);
  stateRef.current = state;

  // Catalog mirror, so async callbacks can reconcile a gateway id without
  // doing it inside a setState updater (React defers those to the render
  // phase, so anything the callback needs afterwards is not yet computed).
  const modelsRef = useRef<readonly ModelInfo[]>([]);
  modelsRef.current = state.models;

  const gatewayDefaultsFetchedConnectionRef = useRef(false);
  // The most recent model the user is known to have run/picked, in memory
  // only — new sessions are seeded from it. After a reload it's rebuilt from
  // the gateway (the most recently updated previous session's model), so the
  // UI still persists nothing; a user with no previous sessions falls back
  // to the gateway default.
  const lastUsedModelIdRef = useRef<string | null>(null);
  // Monotonic tokens so a stale describe (session switched away, or an
  // account change) can never clobber a newer selection.
  const sessionModelFetchSeqRef = useRef(0);
  const modelSwitchSeqRef = useRef(0);
  const modelSwitchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const statusRef = useRef<AgentConnectionStatus>('disconnected');
  statusRef.current = state.status;
  // The gateway the session is currently targeting. Tracked so auto-reconnect
  // retries what the user is actually looking at (e.g. a URL they just switched
  // to that hasn't authenticated yet), not whatever `urlStore` last persisted.
  const agentUrlRef = useRef<string | null>(null);
  agentUrlRef.current = state.agentUrl;

  const knownSessionIdsRef = useRef<Set<string>>(new Set());
  knownSessionIdsRef.current = new Set(state.sessions.map((s) => s.sessionId));

  // Mirror of `state.sessions` for callbacks/effects that need session
  // metadata (e.g. the fresh-session model seeding walks updatedAt) without
  // re-firing on every transcript update.
  const sessionsRef = useRef<readonly ChatSession[]>([]);
  sessionsRef.current = state.sessions;

  const lastSendSessionRef = useRef<string | null>(null);

  const messagesRef = useRef<readonly ChatMessage[]>([]);
  messagesRef.current = state.messages;
  const processingRef = useRef<AgentState['processing']>(null);
  processingRef.current = state.processing;

  // Lazy-load a session's transcript from the gateway when it isn't cached
  // locally (e.g. a chat created on another device — the restore snapshot only
  // bundles the most-recent transcripts; the long tail loads on open). No-ops
  // when local already has messages, the gateway has no `/sync/v1`, or the user
  // switched away before it resolved.
  const ensureRemoteTranscript = useCallback(
    (sessionId: string) => {
      const account = accountIdRef.current;
      const sync = syncRef.current;
      if (account == null || sync == null) return;
      if (historyStore.loadSessionMessages(account, sessionId).length > 0) {
        return;
      }
      fetchAndMergeSessionFromRemote(historyStore, account, sync, sessionId)
        .then((merged) => {
          if (merged == null || merged.length === 0) return;
          if (accountIdRef.current !== account) return;
          setState((prev) =>
            prev.activeSessionId === sessionId
              ? { ...prev, messages: merged }
              : prev,
          );
        })
        .catch(reportError);
    },
    [historyStore, reportError],
  );

  /**
   * Pull the gateway's `/sync/v1` snapshot, merge it into local storage, and
   * reflect the merged sessions/folders (and a changed open transcript) into
   * live state. Shared by the connect-time restore effect and the
   * foreign-frame trigger. Returns null when the snapshot fetch failed or
   * there's no usable sync client (caller decides whether to retry); a
   * non-null result means the merge ran.
   */
  const applyRemoteHydration = useCallback(async () => {
    const account = accountIdRef.current;
    const sync = syncRef.current;
    if (account == null || sync == null) return null;
    const result = await hydrateFromRemote(historyStore, account, sync);
    if (result == null) return null;
    if (accountIdRef.current !== account) return result;
    setState((prev) => ({
      ...prev,
      sessions: result.sessions,
      customFolders: result.folders,
      // Reload the open transcript if the merge changed it.
      messages:
        prev.activeSessionId != null &&
        result.updatedSessionIds.has(prev.activeSessionId)
          ? historyStore.loadSessionMessages(account, prev.activeSessionId)
          : prev.messages,
    }));
    // The open session may not have been bundled in the snapshot — pull its
    // transcript lazily so the active chat isn't left empty.
    const active = activeSessionIdRef.current;
    if (active != null) ensureRemoteTranscript(active);
    return result;
  }, [historyStore, ensureRemoteTranscript]);

  // Single-flight + throttle guards for `requestForeignSessionHydration`.
  const foreignHydrateInFlightRef = useRef(false);
  const lastForeignHydrateAtRef = useRef(0);

  /**
   * A frame arrived for a session this device doesn't know yet (created on
   * another device mid-connection — see `isForeignSessionFrame`). Kick a
   * background snapshot pull so the session materializes in Recents (and its
   * transcript merges in) without waiting for the next reconnect. The frame
   * itself is dropped by the caller; the transcript arrives via sync instead.
   */
  const requestForeignSessionHydration = useCallback(() => {
    if (foreignHydrateInFlightRef.current) return;
    if (
      Date.now() - lastForeignHydrateAtRef.current <
      FOREIGN_HYDRATE_THROTTLE_MS
    ) {
      return;
    }
    foreignHydrateInFlightRef.current = true;
    applyRemoteHydration()
      .catch(reportError)
      .finally(() => {
        foreignHydrateInFlightRef.current = false;
        lastForeignHydrateAtRef.current = Date.now();
      });
  }, [applyRemoteHydration, reportError]);

  /**
   * Look up the session that owns `payload.runId`, lazily registering it
   * the first time we see a frame for a new run.
   */
  const resolveOwner = useCallback(
    (payload: { sessionKey?: string; runId?: string }): string | null => {
      const ourSessionKey = matchKnownSessionKey(
        payload.sessionKey,
        knownSessionIdsRef.current,
      );
      if (ourSessionKey != null && payload.runId != null) {
        runOwnershipRef.current.set(payload.runId, ourSessionKey);
      } else if (
        // Last-send fallback: ONLY for frames that carry no sessionKey at all
        // (early frames of a locally initiated run). A key that is present but
        // unknown means the run belongs to a session from ANOTHER device —
        // claiming it for the last-send session is exactly the cross-device
        // leak (mobile chat landing in an unrelated desktop thread).
        (payload.sessionKey == null || payload.sessionKey.length === 0) &&
        payload.runId != null &&
        !runOwnershipRef.current.has(payload.runId) &&
        lastSendSessionRef.current != null &&
        knownSessionIdsRef.current.has(lastSendSessionRef.current)
      ) {
        runOwnershipRef.current.set(payload.runId, lastSendSessionRef.current);
      }
      return (
        ourSessionKey ??
        (payload.runId != null
          ? (runOwnershipRef.current.get(payload.runId) ?? null)
          : null)
      );
    },
    [],
  );

  /**
   * Attribute a run failure to the chat that owns it. Kept per session and
   * WITHOUT a timer: a run that fails while the user reads another chat — or
   * while they simply look away — must still be discoverable afterwards. It
   * retires when the user dismisses it or retries that chat.
   *
   * Foreign frames (another device's session we have not synced) are dropped,
   * matching `routeMessageFrame`: there is no local chat to attribute them to,
   * and that device surfaces its own failure.
   */
  const reportSessionError = useCallback(
    (
      payload: { sessionKey?: string; runId?: string },
      message: string,
    ): void => {
      if (
        isForeignSessionFrame(
          payload,
          knownSessionIdsRef.current,
          runOwnershipRef.current,
        )
      ) {
        return;
      }
      const ownerId = resolveOwner(payload);
      const sessionId = ownerId ?? activeSessionIdRef.current;
      if (sessionId == null) {
        // Nothing to attach it to (no session at all yet) — the transient
        // surface is the only one available.
        showTransientError(message);
        return;
      }
      setState((prev) =>
        prev.sessionErrors[sessionId] === message
          ? prev
          : {
              ...prev,
              sessionErrors: { ...prev.sessionErrors, [sessionId]: message },
            },
      );
    },
    [resolveOwner, showTransientError],
  );

  /**
   * Resolve which session a frame belongs to and apply a message reducer
   * either to live state (active session) or to a background buffer.
   */
  const routeMessageFrame = useCallback(
    (
      payload: { sessionKey?: string; runId?: string },
      reducer: (messages: readonly ChatMessage[]) => readonly ChatMessage[],
      activeExtra?: {
        processing?: { label: string; detail?: string } | null;
      },
    ): void => {
      if (
        isForeignSessionFrame(
          payload,
          knownSessionIdsRef.current,
          runOwnershipRef.current,
        )
      ) {
        // Another device's session that hasn't synced here yet: drop the
        // frame (its transcript arrives via the sync merge, not the live
        // stream) and pull the snapshot in the background so the session
        // shows up without waiting for the next reconnect.
        requestForeignSessionHydration();
        return;
      }
      const targetId = resolveOwner(payload);
      const activeId = activeSessionIdRef.current;

      if (targetId == null || targetId === activeId) {
        setState((prev) => {
          const next = reducer(prev.messages);
          if (
            next === prev.messages &&
            (activeExtra == null || !('processing' in activeExtra))
          ) {
            return prev;
          }
          return {
            ...prev,
            messages: next,
            ...(activeExtra != null && 'processing' in activeExtra
              ? { processing: activeExtra.processing ?? null }
              : {}),
          };
        });
        return;
      }

      const account = accountIdRef.current;
      if (account == null) return;

      const existing =
        backgroundSessionsRef.current.get(targetId) ??
        historyStore.loadSessionMessages(account, targetId);
      const next = reducer(existing);
      // Apply the processing signal even when the reducer is a message no-op —
      // an error frame can arrive before any assistant delta (nothing to
      // close), and the stashed "Thinking" indicator must still be dropped so
      // a switch-back doesn't show a stale verb spinner.
      if (activeExtra != null && 'processing' in activeExtra) {
        if (activeExtra.processing == null) {
          backgroundProcessingRef.current.delete(targetId);
        } else {
          backgroundProcessingRef.current.set(targetId, activeExtra.processing);
        }
      }
      if (next === existing) return;

      backgroundSessionsRef.current.set(targetId, next);
      setState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.sessionId === targetId ? { ...s, updatedAt: Date.now() } : s,
        ),
      }));

      const stillStreaming = next.some((m) => m.isStreaming === true);
      if (!stillStreaming) {
        historyStore.saveSessionMessages(account, targetId, next);
        backgroundSessionsRef.current.delete(targetId);
        backgroundProcessingRef.current.delete(targetId);
      }
    },
    [resolveOwner, historyStore, requestForeignSessionHydration],
  );

  const connect = useCallback(
    (url?: string, token?: string) => {
      const account = accountIdRef.current;
      if (account == null) return;

      // An explicit connect clears any prior manual-disconnect intent.
      manualDisconnectRef.current = false;
      // Latch synchronously so a concurrent caller (e.g. a second wake event)
      // can't enter a parallel connect while this attempt is still resolving.
      isConnectingRef.current = true;

      const cfg = configRef.current;

      // A caller-supplied pairing token (e.g. the setup form) is persisted to
      // the token store before the socket opens, so the handshake sends it as
      // `auth.token`. Drop any cached device token from a prior pairing so the
      // new token is used instead of a stale one (avoids "token mismatch").
      if (token != null && token.trim().length > 0) {
        urlStore.setAgentToken(account, token.trim());
        urlStore.clearDeviceToken(account);
      }

      // resolvedUrl precedence: explicit arg / config.gatewayUrl, then the
      // host resolver, then the localStorage-backed urlStore.
      void (async () => {
        const explicitUrl = url ?? cfg.gatewayUrl ?? null;
        const resolvedFromHost =
          explicitUrl ?? (await cfg.resolveUrl?.(account)) ?? null;
        const agentUrl =
          resolvedFromHost ?? urlStore.getAgentUrl(account) ?? null;
        if (agentUrl == null || agentUrl.length === 0) {
          isConnectingRef.current = false;
          return;
        }

        // Close existing connection
        if (wsRef.current != null) {
          wsRef.current.close();
        }

        setState((prev) => ({
          ...prev,
          agentUrl,
          status: 'connecting',
          isPaired: deriveIsPaired({
            account,
            url: agentUrl,
            supportsPairingRole: cfg.supportsPairingRole,
            hasStoredToken: urlStore.getAgentToken(account) != null,
            fallbackPairingToken: cfg.fallbackPairingToken,
          }),
        }));

        const ws = createOpenClawClient(agentUrl, {
          clientIdPrefix: cfg.clientIdPrefix,
          accountId: account,
          tokenProvider: urlStore,
          deviceSigner: deviceStore,
          healthStrategy: cfg.healthStrategy ?? 'heartbeat',
          supportsPairingRole: cfg.supportsPairingRole ?? true,
          catalogManifest: cfg.features?.generativeUi
            ? cfg.catalogManifest
            : undefined,
          fallbackPairingToken: cfg.fallbackPairingToken,
          resolveHandshakeToken: cfg.resolveHandshakeToken,
          onDebug: cfg.onConnectionDebug,
        });
        wsRef.current = ws;

        ws.onStatusChange((newStatus, errorMsg) => {
          // The attempt has produced a status; the client now drives `status`
          // (and `statusRef` will track it), so release the in-flight latch.
          isConnectingRef.current = false;
          setState((prev) => ({
            ...prev,
            status: newStatus,
            errorMessage:
              errorMsg ?? (newStatus === 'error' ? prev.errorMessage : null),
          }));

          // Reset the session-context guard whenever we leave 'authenticated'
          // so the next successful auth re-sends the operator identity.
          if (newStatus !== 'authenticated') {
            sessionContextSentForRef.current = null;
          }

          if (newStatus === 'authenticated') {
            // One-time session-context message so the agent knows the
            // operator identity. Bypasses sendMessage so it doesn't appear
            // in visible chat history.
            const dedupKey = cfg.sessionContextDedupKey ?? account;
            const ctx = cfg.sessionContext;
            if (ctx != null && sessionContextSentForRef.current !== dedupKey) {
              try {
                ws.send(buildSessionContextMessage(ctx));
                sessionContextSentForRef.current = dedupKey;
              } catch (err) {
                reportError(err);
              }
            }

            // Fetch recent chat history so reconnects show prior messages.
            fetchChatHistory(ws, setState);

            // No model push on reconnect — the session's modelOverride is
            // persisted on the gateway's disk and survives connection drops.
            // The session-model effect re-READS it (sessions.describe) when
            // status reaches 'authenticated'.

            // Notify consumer so they can persist the successfully-paired
            // URL + cache it locally. Guard so reconnects with the same URL
            // don't hammer their persistence layer.
            if (
              agentUrl.length > 0 &&
              agentUrl !== lastPersistedUrlRef.current
            ) {
              lastPersistedUrlRef.current = agentUrl;
              try {
                cfg.persistAgentUrl?.(agentUrl);
                urlStore.setAgentUrl(account, agentUrl);
              } catch (err) {
                lastPersistedUrlRef.current = null;
                reportError(err);
              }
            }
          }
        });

        ws.onMessage((frame: GatewayFrame) => {
          handleFrame(frame, setState, {
            generativeUiEnabled: generativeUiEnabledRef.current,
            onAppRegister: undefined,
            onAppRemove: undefined,
            onTransientError: showTransientError,
            suppressedModelEchoRunIds: suppressedModelEchoRunIdsRef.current,
            routing: {
              routeMessageFrame,
              reportSessionError,
              isCancelled: (runId) =>
                runId != null && cancelledRunIdsRef.current.has(runId),
              isFrameForActiveSession: (payload) => {
                // Foreign frames (another device's not-yet-synced session)
                // must not drive the active chat's indicators.
                if (
                  isForeignSessionFrame(
                    payload,
                    knownSessionIdsRef.current,
                    runOwnershipRef.current,
                  )
                ) {
                  return false;
                }
                const ownerId = resolveOwner(payload);
                return (
                  ownerId == null || ownerId === activeSessionIdRef.current
                );
              },
              onRunEnd: (runId) => {
                runOwnershipRef.current.delete(runId);
                suppressedModelEchoRunIdsRef.current.delete(runId);
              },
            },
          });
        });
      })().catch((err) => {
        isConnectingRef.current = false;
        reportError(err);
      });
    },
    [
      reportError,
      routeMessageFrame,
      reportSessionError,
      resolveOwner,
      urlStore,
      deviceStore,
      showTransientError,
    ],
  );

  const disconnect = useCallback(() => {
    manualDisconnectRef.current = true;

    const account = accountIdRef.current;
    const cfg = configRef.current;

    if (wsRef.current != null) {
      wsRef.current.close();
      wsRef.current = null;
    }
    // Token-only hosts (no self-service pairing): drop the stored token so the
    // disconnect is durable. A later remount re-reads storage, finds no token,
    // and shows the login screen instead of silently reconnecting. Pairing-role
    // hosts keep their URL-based pairing — there's nothing to drop.
    if (account != null && cfg.supportsPairingRole === false) {
      urlStore.clearAgentToken(account);
    }
    setState((prev) => ({
      ...prev,
      status: 'disconnected',
      processing: null,
      isPaired: deriveIsPaired({
        account,
        url: prev.agentUrl,
        supportsPairingRole: cfg.supportsPairingRole,
        hasStoredToken:
          account != null && urlStore.getAgentToken(account) != null,
        fallbackPairingToken: cfg.fallbackPairingToken,
      }),
    }));
  }, [urlStore]);

  const sendMessage = useCallback(
    (
      content: string,
      opts?: {
        readonly context?: string;
        readonly attachments?: readonly ChatAttachment[];
      },
    ) => {
      if (wsRef.current == null) return;

      const attachments =
        opts?.attachments?.filter((a) => a.content.length > 0) ?? [];
      const hasAttachments = attachments.length > 0;
      if (content.trim().length === 0 && !hasAttachments) return;

      // Guard the frame size BEFORE any state changes: an oversize frame
      // doesn't error, it closes the whole proxy session (1009), which
      // reads as a random disconnect. Surface a real error instead.
      const wirePayload = buildSendPayload(
        configRef.current.systemPreamble,
        content,
        opts?.context,
      );
      if (
        estimateChatSendBytes(wirePayload, attachments) > MAX_CHAT_SEND_BYTES
      ) {
        showTransientError('Message too large to send — remove an attachment.');
        return;
      }

      // The transcript keeps attachment metadata + thumbnail only — never the
      // base64 payload (localStorage quota, 8 MiB sync-doc cap).
      const messageAttachments: readonly MessageAttachment[] = attachments.map(
        (a) => ({
          type: a.type,
          mimeType: a.mimeType,
          ...(a.fileName != null ? { fileName: a.fileName } : {}),
          ...(a.previewUrl != null ? { previewUrl: a.previewUrl } : {}),
        }),
      );

      // Resolve the target session BEFORE touching state. React runs setState
      // updaters eagerly only when the fiber's update queue is empty — when a
      // host calls sendMessage in the same batch as other updates (e.g. a
      // pending-message drain firing on the auth transition), an id assigned
      // inside the updater is still unset after the setState call returns.
      // The frame then went out with no sessionKey, the gateway filed the run
      // under its own main session, and the echoed reply — keyed
      // `agent:main:main`, matching no local session — was dropped as another
      // device's frame, leaving the spinner stuck. The refs are synced here
      // so a second same-batch send reuses the same new session.
      // Minting here means there was no session at all — so no `/model` write
      // can have reached this session yet (a pick made in this state has
      // nowhere to go; see `setSelectedModel`). The seed below carries it.
      const mintedSession = activeSessionIdRef.current == null;
      const targetId = activeSessionIdRef.current ?? generateSessionId();
      activeSessionIdRef.current = targetId;
      knownSessionIdsRef.current.add(targetId);

      const userMsg: ChatMessage = {
        id: generateMessageId('user'),
        role: 'user',
        content,
        timestamp: Date.now(),
        ...(hasAttachments ? { attachments: messageAttachments } : {}),
      };

      // Image-only sends have no text to derive a title from.
      const titleSource =
        content.trim().length > 0
          ? content
          : (attachments[0]?.fileName ?? 'Image');

      setState((prev) => {
        const now = Date.now();
        let { sessions } = prev;

        if (!sessions.some((s) => s.sessionId === targetId)) {
          const newSession: ChatSession = {
            sessionId: targetId,
            folder: defaultFolder,
            title: DEFAULT_SESSION_TITLE,
            pinned: false,
            createdAt: now,
            updatedAt: now,
          };
          sessions = [newSession, ...sessions];
        }

        sessions = sessions.map((s) =>
          s.sessionId === targetId
            ? {
                ...s,
                title:
                  s.title === DEFAULT_SESSION_TITLE
                    ? deriveSessionTitle(titleSource)
                    : s.title,
                updatedAt: now,
              }
            : s,
        );

        // Sending again is the user acting on the failure — retire it rather
        // than leaving a stale banner above a live run.
        const { [targetId]: _cleared, ...remainingErrors } = prev.sessionErrors;
        // The pick (if any) now lives on a real session — retire the pending slot.
        return {
          ...prev,
          sessions,
          activeSessionId: targetId,
          processing: { label: 'Thinking' },
          messages: [...prev.messages, userMsg],
          sessionErrors: remainingErrors,
          pendingModelId: null,
        };
      });

      // A session minted right here has never been told which model to run,
      // and the session-model effect cannot cover it: its seeding needs a
      // `sessions.describe` round-trip, which loses the race to the send
      // below — and by the time it answers, `chat.send` has already created
      // the gateway row with the DEFAULT, so the seeding branch never fires.
      // Push the pick FIRST (same socket, so the gateway applies it before
      // the message) or the first turn silently runs the wrong model.
      if (mintedSession) {
        const seedId = lastUsedModelIdRef.current;
        if (seedId != null) {
          try {
            wsRef.current.send(`/model ${seedId}`, { sessionKey: targetId });
            applySessionModel(targetId, seedId);
          } catch (err) {
            // Nothing seeded — the next read-back shows the truth.
            reportError(err);
          }
        }
      }

      // The wire payload (built above, pre-guard) carries the host's system
      // preamble and optional hidden context; the chat bubble (userMsg above)
      // shows only what the user typed.
      lastSendSessionRef.current = targetId;
      // Claim the run at send time: openclaw adopts the chat.send
      // idempotencyKey as the run's `runId`, so echoed frames resolve to this
      // session by ownership even if their sessionKey doesn't (a gateway that
      // filed the run under its main session). If a gateway mints its own
      // runId instead, the entry simply never matches — harmless.
      const runKey = wsRef.current.send(wirePayload, {
        sessionKey: targetId,
        ...(hasAttachments ? { attachments } : {}),
      });
      runOwnershipRef.current.set(runKey, targetId);
    },
    [defaultFolder, reportError, showTransientError, applySessionModel],
  );

  const clearMessages = useCallback(() => {
    // The active session keeps its slot; only the in-memory messages are
    // emptied (the persist effect rewrites the per-session blob as `[]` as
    // soon as this commits). Stamp `clearedAt` on the active session: the
    // sync hydration path treats it as a transcript-level tombstone
    // (`hydrateFromRemote` drops messages at or before it), so the wipe
    // propagates cross-device instead of being union-merged right back the
    // next time another device holding the old transcript reconciles.
    const clearedAt = Date.now();
    setState((prev) => ({
      ...prev,
      messages: [],
      processing: null,
      sessions:
        prev.activeSessionId == null
          ? prev.sessions
          : prev.sessions.map((s) =>
              s.sessionId === prev.activeSessionId ? { ...s, clearedAt } : s,
            ),
    }));
  }, []);

  const createSession = useCallback(
    ({
      folder,
      isTemporary,
    }: {
      readonly folder: SessionFolder;
      readonly isTemporary?: boolean;
    }): string => {
      const id = generateSessionId();
      const now = Date.now();
      const newSession: ChatSession = {
        sessionId: id,
        folder,
        title: DEFAULT_SESSION_TITLE,
        pinned: false,
        createdAt: now,
        updatedAt: now,
        ...(isTemporary === true ? { isTemporary: true } : {}),
      };
      // Sync the session refs before setState commits so a same-batch
      // sendMessage resolves this session (see sendMessage's comment); the
      // render re-sync keeps them consistent afterwards.
      activeSessionIdRef.current = id;
      knownSessionIdsRef.current.add(id);
      setState((prev) => {
        // Stash outgoing messages whenever the chat has work in flight — a
        // visibly streaming bubble OR a "Thinking" indicator (send already
        // happened, first delta not back yet). Without the second case, an
        // early switch loses the just-sent user message because the delta
        // arrives before the persist effect flushes it.
        const hasInFlight =
          prev.messages.some((m) => m.isStreaming === true) ||
          prev.processing != null;
        if (prev.activeSessionId != null && hasInFlight) {
          backgroundSessionsRef.current.set(prev.activeSessionId, [
            ...prev.messages,
          ]);
          if (prev.processing != null) {
            backgroundProcessingRef.current.set(
              prev.activeSessionId,
              prev.processing,
            );
          }
        }
        return {
          ...prev,
          sessions: [
            newSession,
            ...prev.sessions.filter((s) => s.isTemporary !== true),
          ],
          activeSessionId: id,
          messages: [],
          processing: null,
        };
      });

      // No model push here: the session-model effect (keyed on
      // activeSessionId) sees the fresh session has no gateway row and seeds
      // it with the last-used model — falling back to the gateway default
      // when there is no previous session to inherit from.

      return id;
    },
    [],
  );

  const switchSession = useCallback(
    (sessionId: string) => {
      const account = accountIdRef.current;
      if (account == null) return;
      const cached = backgroundSessionsRef.current.get(sessionId);
      const cachedProcessing =
        backgroundProcessingRef.current.get(sessionId) ?? null;
      const messages =
        cached != null
          ? [...cached]
          : historyStore.loadSessionMessages(account, sessionId);
      if (cached != null) {
        backgroundSessionsRef.current.delete(sessionId);
      }
      backgroundProcessingRef.current.delete(sessionId);
      // Pull the transcript from the gateway if we opened a session we have no
      // local copy of (skip when restoring a still-streaming background chat).
      if (cached == null) ensureRemoteTranscript(sessionId);
      // Sync the active-session ref before setState commits so a same-batch
      // sendMessage targets the switched-to session (see sendMessage's
      // comment). Only for locally known sessions — the updater below ignores
      // unknown ids, so the ref must too.
      if (knownSessionIdsRef.current.has(sessionId)) {
        activeSessionIdRef.current = sessionId;
      }
      setState((prev) => {
        if (!prev.sessions.some((s) => s.sessionId === sessionId)) return prev;
        // Stash outgoing messages whenever the chat has work in flight — a
        // visibly streaming bubble OR a "Thinking" indicator (send already
        // happened, first delta not back yet). Without the second case, an
        // early switch loses the just-sent user message because the delta
        // arrives before the persist effect flushes it.
        const hasInFlight =
          prev.messages.some((m) => m.isStreaming === true) ||
          prev.processing != null;
        if (
          prev.activeSessionId != null &&
          prev.activeSessionId !== sessionId &&
          hasInFlight
        ) {
          backgroundSessionsRef.current.set(prev.activeSessionId, [
            ...prev.messages,
          ]);
          if (prev.processing != null) {
            backgroundProcessingRef.current.set(
              prev.activeSessionId,
              prev.processing,
            );
          }
        }
        return {
          ...prev,
          sessions: prev.sessions.filter(
            (s) => s.isTemporary !== true || s.sessionId === sessionId,
          ),
          activeSessionId: sessionId,
          messages,
          // Restore the indicator if the session has work pending — switching
          // back during "Thinking" should still show the verb spinner.
          processing: cachedProcessing,
          // No model work on switch: `selectedModelId` is derived from
          // `sessionModels[activeSessionId]`, so flipping the active session
          // IS the display update — instantly, from whatever this session's
          // last known model was, with no flicker and no second setState. The
          // session-model effect then refreshes it from the gateway.
        };
      });
    },
    [historyStore, ensureRemoteTranscript],
  );

  const renameSession = useCallback((sessionId: string, title: string) => {
    const trimmed = title.trim();
    if (trimmed.length === 0) return;
    setState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) =>
        s.sessionId === sessionId
          ? { ...s, title: trimmed.slice(0, SESSION_TITLE_MAX_LEN) }
          : s,
      ),
    }));
  }, []);

  const pinSession = useCallback((sessionId: string, pinned: boolean) => {
    setState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) =>
        s.sessionId === sessionId ? { ...s, pinned } : s,
      ),
    }));
  }, []);

  const stopProcessing = useCallback(() => {
    const currentMessages = messagesRef.current;
    const hasStreaming = currentMessages.some((m) => m.isStreaming === true);
    if (processingRef.current == null && !hasStreaming) return;

    const lastStreaming = [...currentMessages]
      .reverse()
      .find((m) => m.isStreaming === true);
    const runId = lastStreaming?.runId;

    if (wsRef.current != null) {
      wsRef.current
        .request('chat.cancel', runId != null ? { runId } : undefined)
        .catch(() => {
          // Gateway doesn't implement chat.cancel — fine.
        });
    }

    setState((prev) => {
      const hasStreamingNow = prev.messages.some((m) => m.isStreaming === true);
      if (prev.processing == null && !hasStreamingNow) return prev;

      const cancelledIds = new Set(prev.cancelledRunIds);
      for (const m of prev.messages) {
        if (m.isStreaming === true && m.runId != null) {
          cancelledIds.add(m.runId);
        }
      }

      const stoppedMsg: ChatMessage = {
        id: generateMessageId('stopped'),
        role: 'assistant' as const,
        content: '_Stopped._',
        timestamp: Date.now(),
      };

      return {
        ...prev,
        processing: null,
        messages: [...closeStreamingMessages(prev.messages), stoppedMsg],
        cancelledRunIds: cancelledIds,
      };
    });
  }, []);

  const deleteSession = useCallback(
    (sessionId: string) => {
      const account = accountIdRef.current;
      if (account != null) {
        historyStore.deleteSessionMessages(account, sessionId);
        // Record a tombstone so the deletion survives a sync merge (otherwise
        // the session resurrects from another device's index) and push it.
        historyStore.addTombstone(account, 'session', sessionId);
        syncRef.current?.queueDeleteSession(sessionId);
        // The sessions-index queue happens via the persistence effect below
        // when `state.sessions` updates (the tombstone is now in the store).
      }

      setState((prev) => {
        const remaining = prev.sessions.filter(
          (s) => s.sessionId !== sessionId,
        );
        // The chat is gone; its recorded failure goes with it.
        const { [sessionId]: _dropped, ...sessionErrors } = prev.sessionErrors;
        const { [sessionId]: _droppedModel, ...sessionModels } =
          prev.sessionModels;
        if (prev.activeSessionId !== sessionId) {
          return { ...prev, sessions: remaining, sessionErrors, sessionModels };
        }
        if (remaining.length === 0) {
          const now = Date.now();
          const fresh: ChatSession = {
            sessionId: generateSessionId(),
            folder: defaultFolder,
            title: DEFAULT_SESSION_TITLE,
            pinned: false,
            createdAt: now,
            updatedAt: now,
          };
          return {
            ...prev,
            sessions: [fresh],
            activeSessionId: fresh.sessionId,
            messages: [],
            processing: null,
            sessionErrors,
            sessionModels,
          };
        }
        const next = [...remaining].sort(
          (a, b) => b.updatedAt - a.updatedAt,
        )[0]!;
        const nextMessages =
          account != null
            ? historyStore.loadSessionMessages(account, next.sessionId)
            : [];
        return {
          ...prev,
          sessions: remaining,
          activeSessionId: next.sessionId,
          messages: nextMessages,
          processing: null,
          sessionErrors,
          sessionModels,
        };
      });
    },
    [defaultFolder, historyStore],
  );

  const setSessionFolder = useCallback(
    (sessionId: string, customFolderId: string | undefined) => {
      // Stamp `folderUpdatedAt` (NOT `updatedAt` — a move isn't chat
      // activity and must not resurface the chat in Recents) so the sync
      // merge propagates the move instead of losing it to any record
      // that's fresher by `updatedAt` on another device.
      const folderUpdatedAt = Date.now();
      setState((prev) => ({
        ...prev,
        sessions: prev.sessions.map((s) =>
          s.sessionId === sessionId
            ? customFolderId == null
              ? (() => {
                  const { customFolderId: _drop, ...rest } = s;
                  return { ...rest, folderUpdatedAt } as ChatSession;
                })()
              : { ...s, customFolderId, folderUpdatedAt }
            : s,
        ),
      }));
    },
    [],
  );

  const createCustomFolder = useCallback((name: string): string => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return '';
    const folder: CustomFolder = {
      id: generateSessionId(),
      name: trimmed,
      createdAt: Date.now(),
    };
    setState((prev) => ({
      ...prev,
      customFolders: [...prev.customFolders, folder],
    }));
    return folder.id;
  }, []);

  const renameCustomFolder = useCallback((folderId: string, name: string) => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    setState((prev) => ({
      ...prev,
      customFolders: prev.customFolders.map((f) =>
        f.id === folderId ? { ...f, name: trimmed } : f,
      ),
    }));
  }, []);

  const pinCustomFolder = useCallback((folderId: string, pinned: boolean) => {
    setState((prev) => ({
      ...prev,
      customFolders: prev.customFolders.map((f) => {
        if (f.id !== folderId) return f;
        if (pinned) return { ...f, pinned: true };
        const { pinned: _drop, ...rest } = f;
        return rest as CustomFolder;
      }),
    }));
  }, []);

  const deleteCustomFolder = useCallback(
    (folderId: string) => {
      const account = accountIdRef.current;
      if (account != null) {
        // Record a folder tombstone so the deletion survives a sync merge
        // — without one, `mergeIndex` resurrects the folder (empty) from
        // any other device's index. Uploaded via the persistence effect's
        // `queueIndex` (tombstones ride in the index doc).
        historyStore.addTombstone(account, 'folder', folderId);
      }
      // The unfiling of member sessions is a folder-assignment change like
      // any other — stamp it so it wins sync merges (see setSessionFolder).
      const folderUpdatedAt = Date.now();
      setState((prev) => ({
        ...prev,
        customFolders: prev.customFolders.filter((f) => f.id !== folderId),
        sessions: prev.sessions.map((s) => {
          if (s.customFolderId !== folderId) return s;
          const { customFolderId: _drop, ...rest } = s;
          return { ...rest, folderUpdatedAt } as ChatSession;
        }),
      }));
    },
    [historyStore],
  );

  const respondToApproval = useCallback(
    (approvalId: string, approved: boolean) => {
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.approval?.approvalId === approvalId
            ? {
                ...msg,
                approval: {
                  ...msg.approval,
                  state: approved ? ('approved' as const) : ('denied' as const),
                },
              }
            : msg,
        ),
      }));
      wsRef.current
        ?.request('approval.respond', { approvalId, approved })
        .catch(reportError);
    },
    [reportError],
  );

  const request: AgentActions['request'] = useCallback(
    async <T,>(
      method: string,
      params?: Record<string, unknown>,
    ): Promise<T> => {
      if (wsRef.current == null) {
        throw new Error('Not connected');
      }
      return wsRef.current.request<T>(method, params);
    },
    [],
  );

  const requestPairing = useCallback(async () => {
    if (wsRef.current == null) {
      throw new Error('Not connected');
    }
    return wsRef.current.requestPairing();
  }, []);

  const sendUiEvent = useCallback(
    (params: {
      readonly surfaceId: string;
      readonly componentId?: string;
      readonly event: string;
      readonly data?: Record<string, unknown>;
    }) => {
      wsRef.current?.sendUiEvent(params);
    },
    [],
  );

  const dismissFullTerminalUi = useCallback(() => {
    setState((prev) => ({ ...prev, fullTerminalUi: null }));
  }, []);

  const dismissSessionError = useCallback((sessionId?: string) => {
    setState((prev) => {
      const target = sessionId ?? prev.activeSessionId;
      if (target == null || prev.sessionErrors[target] == null) return prev;
      const { [target]: _dropped, ...rest } = prev.sessionErrors;
      return { ...prev, sessionErrors: rest };
    });
  }, []);

  const setSelectedModel = useCallback(
    (modelId: string) => {
      const sessionId = activeSessionIdRef.current;
      const isAuthenticated = statusRef.current === 'authenticated';
      const ws = wsRef.current;

      // Re-picking what is already selected would re-send, re-verify and
      // append a SECOND identical confirmation line into persisted history —
      // and the picker offers the checked entry as a live target.
      //
      // Only a no-op when the GATEWAY is known to be on that same id too. A
      // session whose override is a stale/legacy id merely DISPLAYS as its
      // catalog equivalent (reconcileModelIdWithCatalog normalizes it), so
      // re-picking the shown model is the user's one way to realign the
      // gateway — swallowing it would strand them with sends that keep
      // failing against a model the picker claims is active.
      const gatewayKnownId =
        sessionId != null
          ? stateRef.current.sessionModels[sessionId]
          : undefined;
      if (
        selectedModelIdRef.current === modelId &&
        gatewayKnownId === modelId
      ) {
        return;
      }

      // Optimistic: the picker flips immediately (via the derivation) and the
      // gateway is verified below, bouncing back if the switch didn't land.
      // A pick also invalidates any read-back already in flight — the user has
      // just told us the answer, so a describe that started before the click
      // must not land on top of it. That single bump replaces the per-session
      // ownership ref and the unplaced-pick flag this used to need.
      sessionModelFetchSeqRef.current += 1;
      if (sessionId != null) {
        applySessionModel(sessionId, modelId);
      } else {
        // No session yet, so no slot: park it where the derivation ranks it
        // above any resolved default, and `sendMessage` places it for real.
        setState((prev) => ({ ...prev, pendingModelId: modelId }));
      }

      // Record the intent before any bail-out below: an explicit pick is what
      // a fresh session inherits, and a pick made while offline (or with no
      // session yet) is still the model the user asked for.
      lastUsedModelIdRef.current = modelId;

      if (!isAuthenticated || ws == null) return;

      // With no session yet there is nothing to write to. `/model` without a
      // session key lands on the gateway's MAIN session, which is NOT the
      // session `sendMessage` will mint for the first message — writing there
      // moved no model the user will ever run, and verifying it produced a
      // "Model switched to X." line for a session the chat never used. The
      // pick is held as `pendingModelId` (+ last-used) and applied by
      // `sendMessage` when it mints the real session.
      if (sessionId == null) return;

      // `/model <id>` over chat.send is the write path — the gateway rejects
      // `sessions.patch` from webchat clients ("use chat.send for
      // session-scoped updates"). The id already carries the routing-provider
      // prefix (`dt-a/`, `dt-o/`) so it's sent as-is; the chat echo is
      // suppressed by `isModelSwitchEcho` in handleFrame, and the visible
      // confirmation is appended here only after read-back verification.
      try {
        ws.send(`/model ${modelId}`, { sessionKey: sessionId });
      } catch (err) {
        reportError(err);
        return;
      }

      const token = ++modelSwitchSeqRef.current;
      if (modelSwitchTimerRef.current != null) {
        clearTimeout(modelSwitchTimerRef.current);
        modelSwitchTimerRef.current = null;
      }

      const verify = (attempt: number): void => {
        modelSwitchTimerRef.current = setTimeout(
          () => {
            modelSwitchTimerRef.current = null;
            const socket = wsRef.current;
            if (socket == null || modelSwitchSeqRef.current !== token) {
              return;
            }
            socket
              .request<SessionsDescribeResult>('sessions.describe', {
                key: sessionId,
              })
              .then((payload) => {
                if (modelSwitchSeqRef.current !== token) return;
                const gatewayId = composeSessionModelId(payload?.session);
                if (gatewayId == null) {
                  // No row to verify against (pathological — the command send
                  // itself creates the session entry). Keep the optimistic
                  // pick; the next hydrate/switch re-reads the truth.
                  return;
                }
                if (modelIdsMatch(gatewayId, modelId)) {
                  // Cache already holds modelId from the send above.
                  // Confirmation line, only in the chat the switch targeted.
                  setState((prev) =>
                    prev.activeSessionId === sessionId
                      ? {
                          ...prev,
                          messages: [
                            ...prev.messages,
                            {
                              id: generateMessageId('model-pick'),
                              role: 'assistant' as const,
                              content: `Model switched to ${defaultModelLabel(modelId)}.`,
                              timestamp: Date.now(),
                            },
                          ],
                        }
                      : prev,
                  );
                  return;
                }
                if (attempt < MODEL_VERIFY_MAX_ATTEMPTS) {
                  verify(attempt + 1);
                  return;
                }
                // The gateway kept (or substituted) another model — bounce
                // the picker back to the truth and say so. The gateway's own
                // error text (e.g. an invalid/unavailable id) is NOT
                // suppressed by isModelSwitchEcho, so it also shows in chat.
                applySessionModel(sessionId, gatewayId);
                lastUsedModelIdRef.current = gatewayId;
                if (activeSessionIdRef.current === sessionId) {
                  const display = reconcileModelIdWithCatalog(
                    gatewayId,
                    modelsRef.current,
                  );
                  showTransientError(
                    `Couldn't switch to ${defaultModelLabel(modelId)} — still on ${defaultModelLabel(display)}.`,
                  );
                }
              })
              .catch((err: unknown) => {
                // Connection blip mid-verify — keep the optimistic pick; the
                // session-model effect re-reads on the next (re)connect.
                logModelDebug('model-verify-failed', {
                  sessionId,
                  modelId,
                  attempt,
                  error: String(err),
                });
              });
          },
          attempt === 1 ? MODEL_VERIFY_DELAY_MS : MODEL_VERIFY_RETRY_MS,
        );
      };
      verify(1);
    },
    [reportError, showTransientError, logModelDebug, applySessionModel],
  );

  // Drop any in-flight model-switch verification on unmount.
  useEffect(
    () => () => {
      modelSwitchSeqRef.current += 1;
      if (modelSwitchTimerRef.current != null) {
        clearTimeout(modelSwitchTimerRef.current);
        modelSwitchTimerRef.current = null;
      }
    },
    [],
  );

  // `selectedModelId` is derived, so it is stitched in here rather than stored.
  const exposedState = useMemo<AgentState>(
    () => ({ ...state, selectedModelId }),
    [state, selectedModelId],
  );

  const actions = useMemo<AgentActions>(
    () => ({
      sendMessage,
      connect,
      disconnect,
      clearMessages,
      respondToApproval,
      request,
      requestPairing,
      sendUiEvent,
      dismissFullTerminalUi,
      dismissSessionError,
      createSession,
      switchSession,
      renameSession,
      pinSession,
      deleteSession,
      setSessionFolder,
      createCustomFolder,
      renameCustomFolder,
      pinCustomFolder,
      deleteCustomFolder,
      stopProcessing,
      setSelectedModel,
    }),
    [
      sendMessage,
      connect,
      disconnect,
      clearMessages,
      respondToApproval,
      request,
      requestPairing,
      sendUiEvent,
      dismissFullTerminalUi,
      dismissSessionError,
      createSession,
      switchSession,
      renameSession,
      pinSession,
      deleteSession,
      setSessionFolder,
      createCustomFolder,
      renameCustomFolder,
      pinCustomFolder,
      deleteCustomFolder,
      stopProcessing,
      setSelectedModel,
    ],
  );

  // React to account switches: tear down the current session and
  // auto-reconnect if the new account has a stored or resolvable URL.
  const hydratedAccountRef = useRef<string | null>(null);

  // Native session-sync client for the hydrated account (null when the gateway
  // URL is unusable). Disposed + recreated on account/gateway change.
  const syncRef = useRef<TerminalSync | null>(null);
  // Sidecar auth (model catalog + session sync). The host's forward override
  // wins — e.g. the dime cloud routes sidecar calls through its web-api with
  // a DIME JWT, so the browser never holds the gateway credential. Without an
  // override, token-mode gateway auth with the stored agent token (standalone
  // pairing). Null ⇒ no credential right now (session sync treats that as
  // transient and retries).
  const sidecarAuthHeaders = useCallback(
    (account: string): Readonly<Record<string, string>> | null => {
      const sidecar = configRef.current.sidecar;
      if (sidecar != null) {
        const headers = sidecar.getAuthHeaders();
        if (headers == null) return null;
        return { ...headers, 'X-Sync-User': SYNC_USER };
      }
      const token = urlStore.getAgentToken(account);
      if (token == null || token === '') return null;
      return { Authorization: `Bearer ${token}`, 'X-Sync-User': SYNC_USER };
    },
    [urlStore],
  );

  const hydrate = useCallback(() => {
    const account = accountIdRef.current;

    // Signed-out: reset everything.
    if (account == null) {
      if (wsRef.current != null) {
        wsRef.current.close();
        wsRef.current = null;
      }
      hydratedAccountRef.current = null;
      setState({ ...INITIAL_AGENT_STATE });
      return;
    }

    // Already hydrated this account — config updates that arrive later must
    // not tear down the live session.
    if (hydratedAccountRef.current === account) {
      return;
    }

    const cfg = configRef.current;

    void (async () => {
      // localStorage is authoritative for the active browser; fall back to
      // explicit gatewayUrl / the host resolver.
      const storedUrl = urlStore.getAgentUrl(account);
      const resolved =
        cfg.gatewayUrl ?? (await cfg.resolveUrl?.(account)) ?? null;
      const effectiveUrl = storedUrl ?? resolved;

      // Re-check the account hasn't changed underneath the await.
      if (accountIdRef.current !== account) return;

      if (effectiveUrl == null || effectiveUrl.length === 0) {
        if (wsRef.current != null) {
          wsRef.current.close();
          wsRef.current = null;
        }
        hydratedAccountRef.current = null;
        setState({ ...INITIAL_AGENT_STATE });
        return;
      }

      if (wsRef.current != null) {
        wsRef.current.close();
        wsRef.current = null;
      }
      hydratedAccountRef.current = account;
      // Per-account/per-gateway model state must not leak across a switch. The
      // keyed model state itself is reset by the `INITIAL_AGENT_STATE` spread
      // below; these are the out-of-band pieces.
      lastUsedModelIdRef.current = null;
      gatewayDefaultsFetchedConnectionRef.current = false;
      // Invalidate anything the OLD account still has in flight: a pick
      // verification (which would otherwise describe the old session id on the
      // new account's socket) and any read-back, which would land on the new
      // account's freshly reset state.
      modelSwitchSeqRef.current += 1;
      sessionModelFetchSeqRef.current += 1;
      if (modelSwitchTimerRef.current != null) {
        clearTimeout(modelSwitchTimerRef.current);
        modelSwitchTimerRef.current = null;
      }

      // Hydrate sessions + the active session's messages from localStorage.
      const migratedSessions =
        historyStore.migrateLegacyHistoryIfNeeded(account);
      const { sessions: hydratedSessions, recovered } =
        historyStore.recoverOrphanedSessions(account, migratedSessions);
      if (recovered) {
        historyStore.saveSessions(account, hydratedSessions);
      }
      const uiState = historyStore.loadHistoryUiState(account);
      const activeSessionId = resolveActiveSession(
        hydratedSessions,
        uiState.activeSessionId,
      );
      const persistedMessages =
        activeSessionId != null
          ? historyStore.loadSessionMessages(account, activeSessionId)
          : [];
      const persistedFolders = historyStore.loadCustomFolders(account);
      const { folders: hydratedFolders, recovered: foldersRecovered } =
        historyStore.recoverOrphanedFolders(hydratedSessions, persistedFolders);
      if (foldersRecovered) {
        historyStore.saveCustomFolders(account, hydratedFolders);
      }
      // The picker starts EMPTY. The gateway's `/api/models` is the only
      // source of models — there is no built-in fallback catalog — and the
      // SELECTION is not persisted UI-side at all: each session's model lives
      // on the gateway (its `modelOverride`) and is read back silently via
      // `sessions.describe` once the connection authenticates. `modelsStatus`
      // holds the composer's picker slot until the live catalog resolves.
      const paired = deriveIsPaired({
        account,
        url: effectiveUrl,
        supportsPairingRole: cfg.supportsPairingRole,
        hasStoredToken: urlStore.getAgentToken(account) != null,
        fallbackPairingToken: cfg.fallbackPairingToken,
      });
      setState({
        ...INITIAL_AGENT_STATE,
        agentUrl: effectiveUrl,
        isPaired: paired,
        sessions: hydratedSessions,
        customFolders: hydratedFolders,
        activeSessionId,
        messages: persistedMessages,
      });

      if (storedUrl == null) {
        // Cache the URL locally so subsequent reads go through the store.
        urlStore.setAgentUrl(account, effectiveUrl);
      }
      // Only open the socket when paired. Pairing-role hosts count a bare URL
      // as paired (the gateway opens a pairing role for `node.pair.request`);
      // token-only hosts need a token first — otherwise we'd fire a handshake
      // that just fails auth, so the login screen shows instead.
      if (paired) {
        connect(effectiveUrl);
      }

      // Live model catalog is fetched off the `authenticated` status (see the
      // model-fetch effect below), NOT here — the native `/api/models` is
      // token-gated like `/sync/v1`, and a fresh pairing only seeds the gateway
      // token after the handshake resolves.

      // Native session-sync CLIENT only. The restore (snapshot pull) is NOT
      // run here — it's driven by the connection reaching `authenticated` (see
      // the restore effect below), because on a fresh pairing the gateway
      // token isn't seeded until the handshake resolves, so a restore fired
      // now would no-op on missing auth and never retry. Writes queue against
      // this client as soon as it exists.
      syncRef.current?.dispose();
      syncRef.current = createSessionSync({
        store: historyStore,
        accountId: account,
        agentUrl: effectiveUrl,
        baseUrl: configRef.current.sidecar?.baseUrl,
        getAuthHeaders: () => sidecarAuthHeaders(account),
      });
    })().catch(reportError);
  }, [connect, urlStore, historyStore, reportError, sidecarAuthHeaders]);

  // Hydrate on mount + whenever the account or gateway config changes.
  useEffect(() => {
    hydrate();
  }, [config.accountId, config.gatewayUrl, hydrate]);

  // Re-hydrate when the host signals an account change.
  useEffect(() => {
    const onAccountChange = config.onAccountChange;
    if (onAccountChange == null) return undefined;
    return onAccountChange(() => {
      hydrate();
    });
  }, [config.onAccountChange, hydrate]);

  // Restore from the gateway once per authenticated connection. This is the
  // core of the rework: restore is driven by the `authenticated` status — NOT
  // fired at mount — so the gateway token is guaranteed seeded (a fresh
  // pairing only seeds it after the handshake resolves). The ref resets
  // whenever we leave `authenticated`, so a reconnect re-restores; a `null`
  // result (transient failure or a gateway without `/sync/v1`) retries a
  // bounded number of times before settling to local-only.
  const restoredConnectionRef = useRef(false);
  useEffect(() => {
    if (state.status !== 'authenticated') {
      restoredConnectionRef.current = false;
      return undefined;
    }
    const account = accountId;
    if (account == null) return undefined;
    if (hydratedAccountRef.current !== account) return undefined;
    if (restoredConnectionRef.current) return undefined;
    const sync = syncRef.current;
    if (sync == null) return undefined;
    restoredConnectionRef.current = true;

    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = () => {
      // The merge + state application live in `applyRemoteHydration` (shared
      // with the foreign-frame trigger); this effect only owns the bounded
      // retry policy for the connect-time restore.
      applyRemoteHydration()
        .then((result) => {
          if (cancelled || result != null) return;
          attempt += 1;
          if (attempt < RESTORE_MAX_ATTEMPTS) {
            timer = setTimeout(run, RESTORE_RETRY_BASE_MS * attempt);
          }
        })
        .catch(reportError);
    };
    run();
    return () => {
      cancelled = true;
      if (timer != null) clearTimeout(timer);
    };
  }, [state.status, accountId, reportError, applyRemoteHydration]);

  // Live model catalog: fetch once per authenticated connection, mirroring the
  // restore effect — `/api/models` is token-gated like `/sync/v1`, so the
  // gateway token must be seeded (only true after the handshake). Authenticates
  // the same way as the sync client. This is the ONLY source of models, so a
  // failure is retried on a bounded schedule (see the MODELS_* constants) — a
  // cold sidecar 502s and a token can seed a beat after `authenticated`, and a
  // single empty result used to leave the user unable to switch models for the
  // whole connection. `modelsStatus` tracks the outcome so the composer can
  // show a placeholder rather than silently omitting the picker.
  const modelsFetchedConnectionRef = useRef(false);
  useEffect(() => {
    if (state.status !== 'authenticated') {
      modelsFetchedConnectionRef.current = false;
      return undefined;
    }
    const account = accountId;
    if (account == null || hydratedAccountRef.current !== account)
      return undefined;
    if (modelsFetchedConnectionRef.current) return undefined;
    const url = state.agentUrl;
    if (url == null) return undefined;
    // No resolvable HTTP base ⇒ the fetch can never fire (it would return `[]`
    // without a request). Report it as unavailable rather than burning the
    // retry budget on timers that cannot succeed.
    const baseUrl = configRef.current.sidecar?.baseUrl;
    if (baseUrl == null && deriveHttpBase(url) == null) {
      setState((prev) => ({ ...prev, modelsStatus: 'unavailable' }));
      return undefined;
    }
    modelsFetchedConnectionRef.current = true;
    setState((prev) => ({ ...prev, modelsStatus: 'loading' }));

    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const scheduleRetry = () => {
      attempt += 1;
      if (attempt >= MODELS_MAX_ATTEMPTS) {
        // Budget exhausted. Say so instead of leaving the placeholder spinning
        // forever — the picker stays absent, but the UI stops implying it is
        // about to arrive.
        setState((prev) => ({ ...prev, modelsStatus: 'unavailable' }));
        return;
      }
      const delay = Math.min(
        MODELS_RETRY_BASE_MS * attempt,
        MODELS_RETRY_MAX_MS,
      );
      timer = setTimeout(run, delay);
    };

    // An arrow, not a hoisted declaration: the effect's guards above narrow
    // `account`/`url` away from null, and TS only keeps that narrowing for
    // closures created after the guards.
    const run = (): void => {
      fetchGatewayModels(url, {
        baseUrl,
        getAuthHeaders: () => sidecarAuthHeaders(account),
      })
        .then((live: readonly ModelInfo[]) => {
          if (cancelled || accountIdRef.current !== account) return;
          if (live.length === 0) {
            scheduleRetry();
            return;
          }
          // The native /api/models reads openclaw.json, whose labels are the
          // region-stripped id (e.g. "anthropic.claude-opus-4-8") — not the
          // friendly "Opus 4.8". Prettify via the native id→label table,
          // keeping the endpoint's label for ids it doesn't know
          // (defaultModelLabel echoes the id back when unknown).
          const models = live.map((m) => {
            const pretty = defaultModelLabel(m.id);
            return pretty !== m.id ? { ...m, label: pretty } : m;
          });
          // Adopt the catalog for DISPLAY only — nothing is pushed to the
          // gateway here, and nothing needs to be reconciled into a selection:
          // `selectedModelId` is derived from `models`, so simply storing the
          // catalog re-resolves the picker. This block used to hand-roll the
          // reconcile-or-fall-back-to-a-default chain that the derivation now
          // states once.
          setState((prev) => ({ ...prev, models, modelsStatus: 'ready' }));
        })
        .catch(reportError);
    };

    run();
    return () => {
      cancelled = true;
      if (timer != null) clearTimeout(timer);
    };
  }, [
    state.status,
    state.agentUrl,
    accountId,
    reportError,
    sidecarAuthHeaders,
  ]);

  // Per-session model: the gateway is the single source of truth (each
  // session's `modelOverride` lives on its disk), so on hydrate, session
  // switch, and every (re)connect the ACTIVE session's model is read back
  // silently via the `sessions.describe` RPC — never through chat, so
  // nothing shows in the transcript. A session with no gateway row yet (a
  // fresh chat before its first send) is SEEDED with the last-used model:
  // the in-memory last-known pick, or — after a reload — the most recently
  // updated previous session's model, read from the gateway. Only a user
  // with no previous sessions at all falls back to the gateway default
  // (`sessions.list` → `defaults`, fetched once per connection).
  useEffect(() => {
    if (state.status !== 'authenticated') {
      gatewayDefaultsFetchedConnectionRef.current = false;
      return;
    }
    const account = accountId;
    if (account == null || hydratedAccountRef.current !== account) return;
    const ws = wsRef.current;
    if (ws == null) return;
    const sessionId = state.activeSessionId;
    // Single staleness test for everything this effect resolves: a newer
    // effect run OR a user pick bumps the token, and both mean "your answer is
    // out of date". This replaced three separate guards (a per-session
    // ownership ref, an unplaced-pick flag, and a pick-sequence snapshot),
    // each of which had to be checked at the right moment by hand.
    const token = ++sessionModelFetchSeqRef.current;
    const isStale = (): boolean => sessionModelFetchSeqRef.current !== token;

    const applyModelId = (modelId: string | null): void => {
      if (modelId == null || isStale()) return;
      if (sessionId == null) {
        // No session to attribute it to: this is the agent's SHARED main
        // session, so it is display-only — it ranks below a pending pick in
        // the derivation and never becomes what a chat runs.
        setState((prev) =>
          prev.mainSessionModelId === modelId
            ? prev
            : { ...prev, mainSessionModelId: modelId },
        );
        return;
      }
      applySessionModel(sessionId, modelId);
      lastUsedModelIdRef.current = modelId;
    };

    const fetchGatewayDefault = (): void => {
      const cached = stateRef.current.gatewayDefaultModelId;
      if (cached != null) {
        // Already known: the derivation is already using it.
        return;
      }
      if (gatewayDefaultsFetchedConnectionRef.current) return;
      gatewayDefaultsFetchedConnectionRef.current = true;
      ws.request<SessionsListDefaultsResult>('sessions.list', { limit: 1 })
        .then((payload) => {
          const composed = composeSessionModelId(payload?.defaults);
          if (composed != null && !isStale()) {
            setState((prev) => ({ ...prev, gatewayDefaultModelId: composed }));
          }
          // A valid answer carrying no `defaults` means this gateway HAS no
          // default — the flag stays set so we don't re-ask every switch.
        })
        .catch((err: unknown) => {
          // Transient failure, not "no defaults": re-arm so a later session
          // switch or reconnect can try again. Leaving it latched disabled the
          // lookup for the rest of the connection after a single blip.
          gatewayDefaultsFetchedConnectionRef.current = false;
          logModelDebug('model-defaults-failed', { error: String(err) });
        });
    };

    // Seed a gateway-unknown session with an inherited model: push `/model`
    // so the gateway actually RUNS it (display alone would lie), except when
    // it matches the known gateway default — then the default runs anyway
    // and the write (which would create the session's gateway row early) is
    // skipped. Echo suppressed like any programmatic `/model`; no
    // verification pass — the next switch/reconnect re-reads the truth.
    const seedFreshSession = (seedId: string): void => {
      // A pick landed while this seed was resolving: the pick wins outright.
      // Gating only the push (and still applying the display) let a late seed
      // flip the picker away from a pick the user had already seen confirmed.
      if (isStale()) return;
      // Seeding WRITES, so it must never republish a dead id. Both sources are
      // raw gateway values — the in-memory last-used, or a previous session's
      // row — so one session carrying a retired routing prefix (the case the
      // deleted localStorage migration used to repair) would otherwise be
      // copied onto every new chat, where each send then fails with
      // "No API key found for provider <retired>".
      //
      // Resolving through the catalog repairs that in the normal case, since
      // the match is normalized: a `dtmodel/…` row seeds as the live `dt-a/…`
      // id. A seed the catalog no longer carries at all is not propagated —
      // the gateway default is the safe inheritance. An empty catalog means we
      // have no opinion yet, so the raw id stands and the next read corrects.
      const catalog = modelsRef.current;
      let liveSeedId = seedId;
      if (catalog.length > 0) {
        const resolved = resolveModelInCatalog(catalog, seedId);
        if (resolved == null) {
          logModelDebug('model-seed-dropped-unknown', { sessionId, seedId });
          fetchGatewayDefault();
          return;
        }
        liveSeedId = resolved.id;
      }

      const gatewayDefault = stateRef.current.gatewayDefaultModelId;
      const isDefault =
        gatewayDefault != null && modelIdsMatch(gatewayDefault, liveSeedId);
      if (sessionId != null && !isDefault && wsRef.current != null) {
        try {
          wsRef.current.send(`/model ${liveSeedId}`, { sessionKey: sessionId });
        } catch (err) {
          // Send failed — nothing was seeded; the next read shows the truth.
          logModelDebug('model-seed-send-failed', {
            sessionId,
            seedId: liveSeedId,
            error: String(err),
          });
        }
      }
      applyModelId(liveSeedId);
    };

    // Fresh session (no gateway row): inherit the last-used model.
    //  1. In-memory last-known pick (this page load).
    //  2. The most recently updated OTHER session's model, from the gateway.
    //  3. No previous sessions ⇒ the gateway default.
    const resolveFreshSessionModel = (): void => {
      const lastUsed = lastUsedModelIdRef.current;
      if (lastUsed != null) {
        seedFreshSession(lastUsed);
        return;
      }
      const mostRecentOther = sessionsRef.current
        .filter((s) => s.sessionId !== sessionId)
        .reduce<ChatSession | null>(
          (best, s) =>
            best == null || s.updatedAt > best.updatedAt ? s : best,
          null,
        );
      if (mostRecentOther == null) {
        fetchGatewayDefault();
        return;
      }
      ws.request<SessionsDescribeResult>('sessions.describe', {
        key: mostRecentOther.sessionId,
      })
        .then((payload) => {
          const inherited = composeSessionModelId(payload?.session);
          if (inherited != null) {
            seedFreshSession(inherited);
          } else {
            // The previous session never reached the gateway either (e.g. a
            // seeded-as-default chat that was never used) — default.
            fetchGatewayDefault();
          }
        })
        .catch((err: unknown) => {
          logModelDebug('model-inherit-failed', {
            fromSessionId: mostRecentOther.sessionId,
            error: String(err),
          });
          fetchGatewayDefault();
        });
    };

    const describeKey = sessionId ?? ws.getMainSessionKey();
    if (describeKey == null) {
      fetchGatewayDefault();
      return;
    }
    ws.request<SessionsDescribeResult>('sessions.describe', {
      key: describeKey,
    })
      .then((payload) => {
        const modelId = composeSessionModelId(payload?.session);
        if (modelId != null) {
          applyModelId(modelId);
        } else if (sessionId != null) {
          resolveFreshSessionModel();
        } else {
          // No UI session yet at all — just resolve something to display.
          fetchGatewayDefault();
        }
      })
      .catch((err: unknown) => {
        // Transient failure — leave the current display; the next
        // switch/reconnect retries. Logged because this is the read whose
        // failure is indistinguishable from "the model really is this".
        logModelDebug('model-read-failed', {
          sessionId,
          describeKey,
          error: String(err),
        });
      });
  }, [
    state.status,
    state.activeSessionId,
    accountId,
    logModelDebug,
    applySessionModel,
  ]);

  // Persist the active session's messages so a refresh has something to
  // show. Temporary chats are skipped.
  useEffect(() => {
    if (accountId == null) return;
    if (hydratedAccountRef.current !== accountId) return;
    if (state.activeSessionId == null) return;
    const active = state.sessions.find(
      (s) => s.sessionId === state.activeSessionId,
    );
    if (active?.isTemporary === true) return;
    historyStore.saveSessionMessages(
      accountId,
      state.activeSessionId,
      state.messages,
    );
    // Push the transcript (coalesced + debounced by the sync client, so
    // streaming deltas don't spam the gateway). Never upload an EMPTY
    // transcript: a cleared chat propagates via the session's `clearedAt` in
    // the index, and an empty PUT racing a remote write would union-merge
    // the old messages right back.
    if (state.messages.length > 0) {
      syncRef.current?.queueSession(state.activeSessionId, {
        messages: state.messages,
      });
    }
  }, [
    accountId,
    historyStore,
    state.activeSessionId,
    state.messages,
    state.sessions,
  ]);

  // Persist the sessions index whenever it changes.
  useEffect(() => {
    if (accountId == null) return;
    if (hydratedAccountRef.current !== accountId) return;
    historyStore.saveSessions(
      accountId,
      state.sessions.filter((s) => s.isTemporary !== true),
    );
    // Converge the synced index (sessions + folders + tombstones).
    syncRef.current?.queueIndex(
      buildIndexDoc(
        historyStore,
        accountId,
        state.sessions,
        state.customFolders,
      ),
    );
  }, [accountId, historyStore, state.sessions, state.customFolders]);

  // Persist the user's custom folders whenever they change.
  useEffect(() => {
    if (accountId == null) return;
    if (hydratedAccountRef.current !== accountId) return;
    historyStore.saveCustomFolders(accountId, state.customFolders);
    syncRef.current?.queueIndex(
      buildIndexDoc(
        historyStore,
        accountId,
        state.sessions,
        state.customFolders,
      ),
    );
  }, [accountId, historyStore, state.sessions, state.customFolders]);

  // Persist the active session id so a reload re-opens the same chat.
  const lastSavedActiveRef = useRef<string | null>(null);
  useEffect(() => {
    if (accountId == null) return;
    if (hydratedAccountRef.current !== accountId) return;
    if (lastSavedActiveRef.current === state.activeSessionId) return;
    lastSavedActiveRef.current = state.activeSessionId;
    const ui = historyStore.loadHistoryUiState(accountId);
    historyStore.saveHistoryUiState(accountId, {
      ...ui,
      activeSessionId: state.activeSessionId,
    });
  }, [accountId, historyStore, state.activeSessionId]);

  // Auto-reconnect when the user returns to an idle tab. Backgrounded tabs get
  // their timers throttled and the socket silently dropped; `reconnecting-
  // websocket` then burns through its retry budget while hidden and lands in a
  // terminal 'error'/'disconnected' state with nothing left running to recover
  // it (the heartbeat watchdog is a `setInterval` that's torn down on close).
  // So when the tab becomes visible again or the network returns, kick a fresh
  // connect if we're dead. Guards keep this safe: we only fire for an account
  // that already had a resolved gateway (`hydratedAccountRef`), only from a
  // dead status, never when the user hit Disconnect on purpose
  // (`manualDisconnectRef`), never while a connect is already in flight
  // (`isConnectingRef`), and never more than once per `RECONNECT_COOLDOWN_MS`
  // (cross-event backoff so a flapping network / rapid alt-tabbing can't spawn a
  // fresh client per event). Events are debounced because focus +
  // visibilitychange + online tend to fire together on a single wake.
  useEffect(() => {
    const RECONNECT_COOLDOWN_MS = 5000;
    let debounce: ReturnType<typeof setTimeout> | null = null;

    const attemptReconnect = () => {
      if (debounce != null) clearTimeout(debounce);
      debounce = setTimeout(() => {
        debounce = null;
        if (document.visibilityState !== 'visible') return;
        const account = hydratedAccountRef.current;
        if (account == null) return;
        // Don't undo a deliberate Disconnect — only recover passive drops.
        if (manualDisconnectRef.current) return;
        // A connect is already resolving — don't start a second one.
        if (isConnectingRef.current) return;
        const status = statusRef.current;
        if (status !== 'disconnected' && status !== 'error') return;
        // Cross-event backoff: skip if we already fired recently.
        const now = Date.now();
        if (now - lastReconnectAtRef.current < RECONNECT_COOLDOWN_MS) return;
        lastReconnectAtRef.current = now;
        // Reconnect to the gateway the session is currently targeting. Prefer
        // the in-flight `agentUrl` (what the user is looking at — e.g. a URL
        // they just switched to that failed to authenticate, which `urlStore`
        // hasn't persisted yet) over the stored URL. Both normally equal the
        // URL hydrate resolved; passing it explicitly wins over connect's
        // `cfg.gatewayUrl`-first default so the retry can't drift.
        connect(
          agentUrlRef.current ?? urlStore.getAgentUrl(account) ?? undefined,
        );
      }, 300);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') attemptReconnect();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', attemptReconnect);
    window.addEventListener('focus', attemptReconnect);
    return () => {
      if (debounce != null) clearTimeout(debounce);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', attemptReconnect);
      window.removeEventListener('focus', attemptReconnect);
    };
  }, [connect, urlStore]);

  // Cleanup on unmount. Crucially, reset the hydration guard so a *re-mount*
  // (a genuine one, or React StrictMode's dev setup→cleanup→setup double-
  // invoke) re-runs `hydrate()` and reconnects. Without this reset, the
  // cleanup closes the socket but the re-mounted `hydrate()` short-circuits on
  // `hydratedAccountRef`, leaving the UI stuck on 'connecting' with a dead
  // socket (reproduces only on page refresh, where the component mounts fresh).
  useEffect(() => {
    return () => {
      wsRef.current?.close();
      wsRef.current = null;
      syncRef.current?.dispose();
      syncRef.current = null;
      hydratedAccountRef.current = null;
    };
  }, []);

  return (
    <AgentStateProvider value={exposedState}>
      <AgentActionsProvider value={actions}>{children}</AgentActionsProvider>
    </AgentStateProvider>
  );
}

/** Shape of a single message in the `chat.history` RPC response. */
interface HistoryMessage {
  readonly role?: string;
  readonly content?:
    | string
    | ReadonlyArray<{ type?: string; text?: string; mimeType?: string }>;
  readonly timestamp?: number;
}

/**
 * Maximum number of messages OpenClaw's `chat.history` will return in one
 * call. The RPC has no cursor pagination — this is the deepest tail we can
 * pull.
 */
const CHAT_HISTORY_GATEWAY_LIMIT = 1000;

/**
 * Fetch recent chat history from the gateway after authentication. In
 * single-session mode (no active UI session) this seeds the empty chat
 * with the gateway tail. In session mode we skip merging into a
 * user-managed session.
 */
function fetchChatHistory(
  ws: AgentWebSocket,
  setState: Dispatch<SetStateAction<AgentState>>,
): void {
  ws.request<{ messages?: readonly HistoryMessage[] }>('chat.history', {
    // Newer gateways require `sessionKey`. History seeding only applies in
    // single-session mode, so the gateway's main session is the right one.
    sessionKey: ws.getMainSessionKey() ?? undefined,
    limit: CHAT_HISTORY_GATEWAY_LIMIT,
  })
    .then((res) => {
      const seeded = normalizeHistoryMessages(res.messages);
      if (seeded.length === 0) return;

      setState((prev) => {
        if (prev.activeSessionId != null) return prev;
        if (prev.messages.length === 0) {
          return { ...prev, messages: seeded };
        }
        const seen = new Set(prev.messages.map(messageDedupKey));
        const additions = seeded.filter((m) => !seen.has(messageDedupKey(m)));
        if (additions.length === 0) return prev;
        const merged = [...prev.messages, ...additions].sort(
          (a, b) => a.timestamp - b.timestamp,
        );
        return { ...prev, messages: merged };
      });
    })
    .catch(() => {
      // Gateway doesn't support chat.history — silently skip.
    });
}

function messageDedupKey(m: ChatMessage): string {
  return `${m.timestamp}|${m.role}|${m.content}`;
}

function normalizeHistoryMessages(
  raw: readonly HistoryMessage[] | undefined,
): ChatMessage[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((m: HistoryMessage, i: number) => {
    let text = '';
    let attachments: MessageAttachment[] | undefined;
    if (typeof m.content === 'string') {
      text = m.content;
    } else if (Array.isArray(m.content)) {
      const blocks = m.content as ReadonlyArray<{
        type?: string;
        text?: string;
        mimeType?: string;
      }>;
      text = blocks
        .filter((c) => c.type === 'text' && c.text != null)
        .map((c) => c.text!)
        .join('');
      // Image blocks survive as metadata chips — the history payload has no
      // thumbnail, but an image-only message must not render as empty.
      const images = blocks.filter((c) => c.type === 'image');
      if (images.length > 0) {
        attachments = images.map((c) => ({
          type: 'image' as const,
          mimeType: c.mimeType ?? 'image/*',
        }));
      }
    }
    const msg: ChatMessage = {
      id: `history-${m.timestamp ?? 'x'}-${i}`,
      role: m.role === 'user' ? 'user' : 'assistant',
      content: text,
      timestamp: m.timestamp ?? Date.now(),
      ...(attachments != null ? { attachments } : {}),
    };
    return msg;
  });
}
